/*
 * <sf-vehicle-picker>: the "Complete Your Vehicle" drawer. Type (Car Covers / Seat Covers / Floor Mats) -> Year
 * -> Make -> Model -> GO, cloned from the competitor: right-hand drawer with custom lists on desktop, bottom sheet
 * with native <select>s on phones (CSS shows one control set per viewport; this script keeps both in sync).
 * Opens on document 'seatfit:open-picker' {prefill}; the core turns every #find-your-fit link and
 * [data-seatfit-open-picker] element into that event. Type starts on the page's product and picks whose vehicle
 * list the fields offer; GO opens that product's page with the vehicle.
 * Helpers load first (defer order): seatfit-vehicle-picker-lists.js, -kind.js, -combobox.js, -dom.js.
 */
(function () {
  'use strict';
  if (customElements.get('sf-vehicle-picker')) return;
  var L = window.SeatFitPickerLists;
  var K = window.SeatFitPickerKind;
  var Combo = window.SeatFitPickerCombo;
  var D = window.SeatFitPickerDom;
  if (!L || !K || !Combo || !D) {
    console.warn('[SeatFit picker] helper scripts did not load; the vehicle picker is disabled');
    return;
  }

  var KEYS = ['type', 'year', 'make', 'model'];
  var LOAD_TIMEOUT_MS = 15000;
  var DOC_EVENTS = ['seatfit:open-picker', 'focusin', 'shopify:section:select', 'shopify:section:deselect'];

  function empty() { return { year: null, make: null, model: null }; }

  class SfVehiclePicker extends HTMLElement {
    constructor() {
      super();
      this.pick = empty();
      this.pending = null; // prefill waiting for its list (make/model wait for a year)
      this.kind = null; // product in the Type field; this.index is that product's vehicle list
      this.index = null;
      this.loading = null;
      this.trigger = null;
      this.onDocEvent = this.onDocEvent.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
    }

    connectedCallback() {
      if (!this.panel) this.setup();
      var self = this;
      DOC_EVENTS.forEach(function (t) { document.addEventListener(t, self.onDocEvent); });
      window.addEventListener('pageshow', this.onPageShow);
      this.render();
      // Links from other pages (…/pages/seat-covers-leather#find-your-fit) arrive with the hash.
      if (location.hash === '#find-your-fit') this.open(null);
    }

    disconnectedCallback() {
      var self = this;
      DOC_EVENTS.forEach(function (t) { document.removeEventListener(t, self.onDocEvent); });
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.isOpen()) D.lockScroll(false); // an editor re-render must not leave the page unscrollable
    }

    setup() {
      var self = this;
      this.panel = this.querySelector('.sf-vp__panel');
      this.goBtn = this.querySelector('.sf-vp__go');
      this.errorEl = this.querySelector('.sf-vp__error');
      this.emptyLabel = this.getAttribute('data-empty') || '';
      this.kind = this.homeKind = K.initial(this);
      this.hooks = {
        busy: function (on) { self.classList.toggle('is-loading', on); },
        failed: function (on) { if (self.errorEl) self.errorEl.hidden = !on; }
      };
      this.fields = D.collectFields(this, Combo, function (k, v, fromCombo) { self.choose(k, v, fromCombo); });
      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-sf-vp-close]')) self.close();
        // Try again hides with the error: park focus on the dialog so Esc/Tab keep working.
        else if (e.target.closest('[data-sf-vp-retry]')) { self.panel.focus({ preventScroll: true }); self.open(null); }
      });
      this.addEventListener('keydown', function (e) {
        if (!self.isOpen()) return;
        if (e.key === 'Escape') { e.preventDefault(); self.close(); }
        else if (e.key === 'Tab') D.trapTab(e, self.panel);
      });
      if (this.goBtn) this.goBtn.addEventListener('click', function () { self.go(); });
    }

    isOpen() { return this.hasAttribute('open'); }

    onDocEvent(e) {
      var d = e.detail || {};
      if (e.type === 'focusin') {
        // Focus that escapes the open dialog (e.g. a script focusing the page) is pulled back.
        if (this.isOpen() && !this.contains(e.target)) this.panel.focus({ preventScroll: true });
      } else if (e.type === 'seatfit:open-picker') {
        if (d.sfPickerHandled) return; // a second picker section on the page must not open on top
        d.sfPickerHandled = true;
        this.open(d.prefill || null);
      } else if (d.sectionId === this.getAttribute('data-section-id')) {
        // Theme editor: selecting the section shows the drawer so the owner can style it.
        if (e.type === 'shopify:section:select') this.open(null); else this.close();
      }
    }

    // Back button from the product page restores this page from bfcache with GO still "going".
    onPageShow(e) {
      if (!e.persisted) return;
      this.classList.remove('is-going');
      this.render();
    }

    // An explicit prefill (Edit Vehicle, a vehicle card) replaces the draft and brings Type back to the page's
    // product; a plain open keeps the draft from the last open, or starts from the vehicle already in the URL.
    open(prefill) {
      var SF = window.SeatFit;
      var self = this;
      if (prefill) {
        this.pick = empty();
        this.pending = { year: prefill.year, make: prefill.make, model: prefill.model };
        K.set(this, this.homeKind);
      } else if (!this.pick.year && !this.pending && SF && SF.readParams) {
        var p = SF.readParams();
        if (p.year || p.make || p.model) this.pending = { year: p.year, make: p.make, model: p.model };
      }
      if (!this.isOpen()) {
        var a = document.activeElement;
        this.trigger = a && a !== document.body ? a : null;
        this.setAttribute('open', '');
        D.lockScroll(true);
        this.panel.focus({ preventScroll: true });
      }
      this.render();
      this.load().then(function (idx) {
        if (!idx) return;
        self.settle();
        self.render();
        if (self.isOpen() && document.activeElement === self.panel) self.focusNext(null);
      });
    }

    close() {
      if (!this.isOpen()) return;
      var self = this;
      KEYS.forEach(function (k) { if (self.fields[k]) self.fields[k].combo.close(); });
      this.removeAttribute('open');
      D.lockScroll(false);
      var t = this.trigger;
      this.trigger = null;
      if (t && t.focus && document.contains(t)) t.focus({ preventScroll: true });
    }

    load() { return K.load(this, LOAD_TIMEOUT_MS, this.hooks); }

    // Type picked: the fields wait for that product's list, then keep the year/make/model it still offers.
    setKind(kind, fromCombo) {
      var self = this;
      var moved = K.set(this, kind);
      this.render();
      this.load().then(function (idx) {
        if (!idx) return;
        if (moved) { self.settle(); self.render(); }
        if (fromCombo) self.focusNext('type');
      });
    }

    settle() {
      if (!this.index) return;
      var s = L.settle(this.index, this.pick, this.pending);
      this.pick = s.pick;
      this.pending = s.pending;
    }

    choose(key, value, fromCombo) {
      if (key === 'type') return this.setKind(value, fromCombo);
      if (this.index) {
        if (key !== 'year') this.pending = null; // the shopper's own make/model beat a waiting prefill
        this.pick[key] = value || null;
        this.settle();
      }
      this.render();
      if (fromCombo && value) this.focusNext(key);
    }

    render() {
      if (!this.fields) return;
      var type = this.fields.type;
      var st = L.fieldStates(this.index, this.pick, type ? type.typeOptions : null, this.kind);
      var self = this;
      KEYS.forEach(function (k) { if (self.fields[k]) D.renderField(self.fields[k], st[k], self.emptyLabel); });
      // While another product's list loads, the kept vehicle is not checked against it yet: no GO.
      var complete = !!(this.index && this.pick.year && this.pick.make && this.pick.model);
      if (this.goBtn) this.goBtn.disabled = !complete || this.classList.contains('is-going');
      this.toggleAttribute('data-complete', complete);
    }

    focusNext(afterKey) { D.focusNext(this.fields, KEYS, afterKey, this.goBtn); }

    go() {
      var p = this.pick;
      if (!(this.index && p.year && p.make && p.model)) return;
      var href = L.goUrl(K.productUrl(this.kind), p, location.href);
      this.classList.add('is-going'); // spinner in GO while the product page loads
      if (this.goBtn) this.goBtn.disabled = true;
      window.location.assign(href);
    }
  }

  customElements.define('sf-vehicle-picker', SfVehiclePicker);
})();
